import './index.css';

import type { VueQueryPluginOptions } from 'vue-query';
import type { Component } from 'vue';

import { VueQueryPlugin } from 'vue-query';
import { sentryCaptureException } from './exceptions/sentry';
import * as Sentry from '@sentry/vue';
import { createApp } from 'vue'; // CHANGE

import App from './App.vue';
import '@modules/libraries';
import '@modules/editor-sidebar';
import store from '@/store';
import PerfectScrollbar from 'vue3-perfect-scrollbar';
import 'vue3-perfect-scrollbar/dist/vue3-perfect-scrollbar.css';
import VueClickAway from 'vue3-click-away';
import lazyload from '@/plugins/lazyload';
import tooltipV2 from '@/plugins/tooltip-v2';
import directives from './global/directives';
import tinycolor from 'tinycolor2';
import mixins from './global/mixins';
import Popover from '@/core/components/Popover';
import MasonryWall from '@yeger/vue-masonry-wall';
import i18n from './i18n';

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: 3,
        onError: (err: any) => {
          const errors = err?.response?.errors;
          sentryCaptureException('Query: API fail', JSON.stringify(err), { errors }, { level: 'log' });
        },
      },
      mutations: {
        onError(error, variables, context) {
          sentryCaptureException(
            'Mutation: API fail',
            JSON.stringify(error),
            { error, variables, context },
            { level: 'log' },
          );
        },
      },
    },
  },
};
const vueApp = createApp({
  render: () => h(App),
});

vueApp.config.globalProperties.$tinycolor = tinycolor;
/**
 * Import base component
 */
const components = import.meta.glob<true, string, { default: Component }>('./core/components/base/*.vue', {
  eager: true,
});

Object.entries(components).forEach(([filePath, definition]) => {
  const componentName = filePath
    .split('/')
    .pop()
    ?.replace(/\.\w+$/, '');
  if (componentName) {
    vueApp.component(componentName, definition.default);
  }
});

vueApp.use(VueQueryPlugin, vueQueryPluginOptions);
vueApp.use(i18n);
vueApp.use(store);
vueApp.use(PerfectScrollbar);
vueApp.use(VueClickAway);
vueApp.use(MasonryWall);
vueApp.use(tooltipV2);
vueApp.use(lazyload);
vueApp.use(Popover, { tooltip: true });
directives(vueApp);
mixins(vueApp);

if (import.meta.env.VITE_APP_MODE === 'development') {
  vueApp.config.performance = true;
}

vueApp.mount('#app');

const SentryInit = (app: any) => {
  Sentry.init({
    app,
    release: import.meta.env.VITE_APP_VERSION || 1,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    hooks: ['activate', 'create', 'destroy', 'mount', 'update'],
    tracesSampleRate: import.meta.env.VITE_APP_MODE === 'production' ? 0.2 : 1,
    beforeSend(event) {
      return event;
    },
    integrations: [
      new Sentry.BrowserTracing({
        startTransactionOnLocationChange: true,
        // shouldCreateSpanForRequest: true,
        traceFetch: true,
        traceXHR: true,
        shouldCreateSpanForRequest: () => {
          return true;
        },
      }),
      new Sentry.Replay({
        // Additional SDK configuration goes in here, for example:
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    trackComponents: true,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_APP_MODE,
    enabled: true,
  });
};
SentryInit(vueApp);
